import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { requestOtp, verifyOtp } from "../api/endpoints/auth"
import { useAuth } from "../stores/auth"

export function LoginPage() {
  const navigate = useNavigate()
  const { setSession } = useAuth()
  const [step, setStep] = useState<"phone" | "otp">("phone")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otp, setOtp] = useState("")
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleRequestOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await requestOtp(phoneNumber)
      setFeedback(response.data.message)
      setStep("otp")
    } catch (requestError) {
      setError((requestError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await verifyOtp(phoneNumber, otp)
      setSession(response.data.sessionToken, response.data.user, response.data.expiresAt)
      navigate(response.data.user.defaultPath ?? "/")
    } catch (verifyError) {
      setError((verifyError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 px-4 py-8 sm:px-6">
      <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg sm:p-8">
        <div className="text-xs font-bold uppercase tracking-widest text-blue-600">Admin Access</div>
        <h1 className="mt-2 mb-1 text-2xl font-bold text-slate-800 sm:text-3xl">
          LoanCore operations portal
        </h1>
        <p className="mb-5 text-sm text-slate-500">
          Sign in with your registered phone number and one-time password.
        </p>

        {feedback ? (
          <div className="mb-4 rounded-r border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-800">
            {feedback}
          </div>
        ) : null}
        {error ? (
          <div className="mb-4 rounded-r border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        {step === "phone" ? (
          <form className="flex flex-col gap-4" onSubmit={handleRequestOtp}>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-700">Phone number</span>
              <input
                autoComplete="tel"
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="+254700000000"
                required
                type="tel"
                value={phoneNumber}
              />
            </label>
            <button className="w-full rounded-lg bg-blue-600 py-2.5 font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Sending OTP..." : "Request OTP"}
            </button>
          </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleVerifyOtp}>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-700">One-time password</span>
              <input
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setOtp(event.target.value)}
                placeholder="123456"
                required
                value={otp}
              />
            </label>
            <button className="w-full rounded-lg bg-blue-600 py-2.5 font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Verifying..." : "Verify OTP"}
            </button>
            <button className="w-full rounded-lg py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100" onClick={() => setStep("phone")} type="button">
              Change phone number
            </button>
          </form>
        )}
      </section>
    </div>
  )
}
